/**
 * @vra/signing — the dual signature of spec §4.1.2: an eSign envelope over
 * the canonical digest (MOCK in Phase I/II) plus a parallel ECDSA
 * (secp256k1) signature over the same digest, verifiable on the ledger.
 *
 * Browser- and Node-compatible (noble libraries, no node:crypto), because
 * signing happens on the depot device at capture time (§12.3).
 */
import * as secp from "@noble/secp256k1";
import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils";
import { canonicalSha256, canonicalSha256Hex, } from "@vra/canonical";
// RFC 6979 deterministic nonces (required for sync signing with noble v2)
secp.etc.hmacSha256Sync = (k, ...m) => hmac(sha256, k, secp.etc.concatBytes(...m));
export function generateKeyPair() {
    const priv = secp.utils.randomPrivateKey();
    return {
        privateKeyHex: bytesToHex(priv),
        publicKeyHex: bytesToHex(secp.getPublicKey(priv, true)),
    };
}
/** Sign a 32-byte digest (hex). Returns 64-byte compact signature, hex. */
export function signDigest(digestHex, privateKeyHex) {
    if (digestHex.length !== 64)
        throw new Error("digest must be 32 bytes hex");
    const sig = secp.sign(hexToBytes(digestHex), hexToBytes(privateKeyHex));
    return sig.toCompactHex();
}
export function verifyDigest(digestHex, signatureHex, publicKeyHex) {
    try {
        return secp.verify(secp.Signature.fromCompact(signatureHex), hexToBytes(digestHex), hexToBytes(publicKeyHex));
    }
    catch {
        return false;
    }
}
export class MockESign {
    async sign(digestSha256Hex, officerRef) {
        return {
            envelopeType: "MOCK_ESIGN_XADES_T",
            digestSha256Hex,
            officerRef,
            signedAtIso: new Date().toISOString(),
            mockToken: bytesToHex(sha256(new TextEncoder().encode(digestSha256Hex + officerRef))).slice(0, 32),
        };
    }
}
/** Canonicalize → digest → dual-sign. This is the capture-time operation
 *  that fixes content and timestamp before upload (§12.3). */
export async function signArtefact(artefact, keys, officerRef, eSignAdapter = new MockESign()) {
    const digestBytes = canonicalSha256(artefact);
    const digestHex = canonicalSha256Hex(artefact);
    const sig = secp.sign(digestBytes, hexToBytes(keys.privateKeyHex));
    return {
        artefact,
        digestSha256Hex: digestHex,
        ecdsaSignatureHex: sig.toCompactHex(),
        signerPublicKeyHex: keys.publicKeyHex,
        eSign: await eSignAdapter.sign(digestHex, officerRef),
    };
}
/** Full verification: recompute the canonical digest from the artefact,
 *  then check the ECDSA signature. Any byte drift fails here. */
export function verifyArtefact(s) {
    const recomputed = canonicalSha256Hex(s.artefact);
    if (recomputed !== s.digestSha256Hex) {
        return { ok: false, reason: `digest mismatch: recomputed ${recomputed}` };
    }
    if (!verifyDigest(s.digestSha256Hex, s.ecdsaSignatureHex, s.signerPublicKeyHex)) {
        return { ok: false, reason: "ECDSA signature invalid" };
    }
    if (s.eSign.digestSha256Hex !== s.digestSha256Hex) {
        return { ok: false, reason: "eSign envelope digest mismatch" };
    }
    return { ok: true };
}
